"use client"

import * as React from "react"
import { cn } from "../lib/utils"
import type { AssessmentItem } from "../schema"
import type { EvaluationResult } from "../types"
import { HTMLContent } from "./shared/html-content"

// ============================================================================
// Types
// ============================================================================

export interface QtiFeedbackBlockProps {
	/** The evaluation result containing feedback info */
	result: EvaluationResult
	/** The assessment item containing feedback blocks */
	item: AssessmentItem
	/** Custom class name */
	className?: string
}

// ============================================================================
// QTI Feedback Block Component
// ============================================================================

/**
 * Renders the appropriate feedback block based on evaluation results.
 *
 * This component handles:
 * - Finding the correct feedback block to display
 * - Styling based on correct/incorrect status
 * - Sanitizing and rendering the feedback HTML
 */
export function QtiFeedbackBlock({ result, item, className }: QtiFeedbackBlockProps) {
	// Find the feedback block to show
	const feedbackBlock = React.useMemo(() => {
		if (!result.feedbackIdentifier) {
			return null
		}

		return item.itemBody.feedbackBlocks.find((fb) => fb.identifier === result.feedbackIdentifier)
	}, [result.feedbackIdentifier, item.itemBody.feedbackBlocks])

	// If no feedback to show, return null
	if (!feedbackBlock) {
		return null
	}

	const isCorrect = result.status === "correct"

	return (
		<div className={cn("qti-feedback p-6", className)} data-correct={isCorrect}>
			{/* Feedback header */}
			<div className="flex items-center gap-3 mb-4">
				<div
					className="qti-feedback-icon flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
					data-correct={isCorrect}
				>
					{isCorrect ? (
						<svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
							<title>Correct</title>
							<path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
						</svg>
					) : (
						<svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
							<title>Incorrect</title>
							<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
						</svg>
					)}
				</div>
				<div className="flex-1">
					<h3
						className="text-xl font-extrabold"
						style={{ color: isCorrect ? "var(--qti-feedback-correct-text)" : "var(--qti-feedback-incorrect-text)" }}
					>
						{isCorrect ? "Correct!" : "Incorrect"}
					</h3>
					<p className="text-sm text-muted-foreground">{isCorrect ? "Nice work!" : "Try again"}</p>
				</div>
			</div>

			{/* Feedback content */}
			<HTMLContent html={feedbackBlock.contentHtml} className="text-foreground" />
		</div>
	)
}

export default QtiFeedbackBlock
